import { ReactElement } from 'react';
import { FormikContextType } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import withComponentLoader from '../../../../hoc/withComponentLoader';
import AppWrappedInput from './AppWrappedInput';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import useDebounce from '../../../../utils/hooks/useDebounce';

/**
 * Searchable text input base component stylized to work with Custom App Request Form
 * @param {Object} props component properties
 * @param {Function} props.setLoading setter function coming from `withComponentLoader` HOC
 * @param {string} props.label display text
 * @param {string} props.sublabel optional secondary display text to show as default placeholder on input
 * @param {FormikContextType} props.formik full Formik Context
 * @param {string} props.displayValue value display override; if not provided, defaults to `formik.values[props.name]`
 * @param {Function} props.onSearchHandler function to query for list of options based on user input
 * @param {Function} props.onSearchSuccess function to handle successful returns from API during query
 * @param {Function} props.onSearchFailure function to handle failures from API during query
 * @param {string|ReactElement} props.helperText helper text
 * @param {string[]|Object[]} props.options list of options to display.  Should be list of strings or list of objects matching `{[key]: [value]}` pattern
 * @param {boolean} props.isRequired flag to determine whether to show required indicator
 * @param {boolean} props.isLoading flag to indicate the loading state (comes from parent)
 * @param {object} props.props the rest of the props
 * @returns Searchable text input stylized for custom
 */
const AppWrappedSearchableAutocomplete = ({
	setLoading,
	label,
	sublabel = null,
	formik,
	displayValue,
	onSearchHandler,
	onSearchSuccess,
	onSearchFailure,
	helperText,
	isDisabled = false,
	isRequired = false,
	isLoading = false,
	...props
}) => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState([]);
	const debouncedSearchTerm = useDebounce(inputValue, 500);

	const onSearch = async val => {
		try {
			if (val) {
				const value = String(val).trim();
				const resultsArr = await onSearchHandler(value);
				//transform for Autocomplete's consumption
				return onSearchSuccess(resultsArr);
			} else {
				return onSearchSuccess([]);
			}
		} catch (error) {
			onSearchFailure(error);
		}
	};

	useEffect(() => {
		setLoading(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	useEffect(() => {
		const fetch = async () => {
			const searchResults = await onSearch(inputValue);
			setOptions(searchResults ?? []);
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchTerm]);

	return (
		<AppWrappedInput
			label={label}
			error={
				formik.touched[props.name] ? formik.errors[props.name] : null
			}
			isRequired={isRequired}
		>
			<FormControl variant='filled' fullWidth>
				<Autocomplete
					options={options}
					clearIcon={null}
					filterOptions={options => options}
					groupBy={option => option.category}
					getOptionLabel={option =>
						option.key ? option.key : option
					}
					onChange={(_, newValue) => {
						formik.setFieldValue(
							props.name,
							(newValue?.value ? newValue.value : newValue) ?? ''
						);
					}}
					onInputChange={(_, newInputValue) => {
						setInputValue(newInputValue);
					}}
					autoComplete
					noOptionsText={
						inputValue.length > 0
							? 'No options matching your search'
							: 'Please type to search...'
					}
					value={displayValue ?? formik.values[props.name]}
					isOptionEqualToValue={(option, value) => {
						return option.key
							? option.key === value || option.value === value
							: option === value;
					}}
					disabled={isDisabled}
					disablePortal={true}
					renderInput={params => (
						<TextField
							{...params}
							fullWidth
							variant='filled'
							placeholder={
								sublabel
									? sublabel
									: 'Search for ' + label + '...'
							}
							name={props.name}
							InputLabelProps={{ shrink: false }}
							InputProps={{
								...params.InputProps,
								disableUnderline: true,
							}}
							onBlur={formik.handleBlur}
							disabled={isDisabled}
							{...props}
						/>
					)}
					renderOption={(props, option, { inputValue }) => {
						const optionStr = option.key ? option.key : option;
						const matches = match(optionStr, inputValue, {
							insideWords: true,
							findAllOccurrences: true,
						});
						const parts = parse(optionStr, matches);

						return (
							<li {...props}>
								<div>
									{parts.map((part, index) => (
										<span
											key={index}
											style={{
												fontWeight: part.highlight
													? 700
													: 400,
											}}
										>
											{part.text}
										</span>
									))}
								</div>
							</li>
						);
					}}
				/>
				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</AppWrappedInput>
	);
};
export default withComponentLoader(AppWrappedSearchableAutocomplete);
